import type {
  PlaybookScope,
  TopicsPageFilter,
} from "@repo/app-types/entities/filters";
import type {
  CustomerExperienceTrendData,
  TopicAnalysisView,
  TopicDrilldownStats,
  TopicId,
  TopicMatchesResponse,
  TopicsOverviewResponse,
  WinRateTrendData,
} from "@repo/app-types/entities/topic";
import { RECENT_MATCHES_DISPLAY_LIMIT } from "../../features/transcriptViewer/matchListItem.js";
import { TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

export interface TopicStatsQueryArg {
  scope: PlaybookScope;
  filters: readonly TopicsPageFilter[];
}

export interface TopicDrilldownQueryArg extends TopicStatsQueryArg {
  topicId: TopicId;
}

const topicApi = api.injectEndpoints({
  endpoints: (builder) => ({
    topicsOverview: builder.query<TopicsOverviewResponse, TopicStatsQueryArg>({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/topics/overview`,
        body: { scope, filters },
      }),
      providesTags: (result) => [
        { type: TagType.Topic, id: "LIST" },
        ...(result?.topics.map((t) => ({
          type: TagType.Topic,
          id: t.topicId,
        })) ?? []),
      ],
    }),
    topicsCustomerExperienceTrend: builder.query<
      CustomerExperienceTrendData,
      TopicStatsQueryArg
    >({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/topics/customer-experience-trend`,
        body: { scope, filters },
      }),
      providesTags: () => [
        { type: TagType.Topic, id: "CUSTOMER_EXPERIENCE_TREND" },
      ],
    }),
    topicsWinRateTrend: builder.query<WinRateTrendData, TopicStatsQueryArg>({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/topics/win-rate-trend`,
        body: { scope, filters },
      }),
      providesTags: () => [{ type: TagType.Topic, id: "WIN_RATE_TREND" }],
    }),
    topicDrilldown: builder.query<TopicDrilldownStats, TopicDrilldownQueryArg>({
      query: ({ scope, filters, topicId }) => ({
        method: "POST",
        url: `/topics/${encodeURIComponent(topicId)}/detail`,
        body: { scope, filters },
      }),
      providesTags: (_result, _error, { topicId }) => [
        { type: TagType.Topic, id: topicId },
      ],
    }),
    topicMatches: builder.query<TopicMatchesResponse, TopicDrilldownQueryArg>({
      query: ({ scope, filters, topicId }) => ({
        method: "POST",
        url: `/topics/${encodeURIComponent(topicId)}/matches`,
        body: {
          scope,
          filters,
          limit: RECENT_MATCHES_DISPLAY_LIMIT,
        },
      }),
      providesTags: (_result, _error, { topicId }) => [
        { type: TagType.Topic, id: topicId },
      ],
    }),
    topicAnalysis: builder.query<TopicAnalysisView | null, TopicId>({
      query: (topicId) => ({
        method: "GET",
        url: `/topics/${encodeURIComponent(topicId)}/analysis`,
      }),
      providesTags: (_result, _error, topicId) => [
        { type: TagType.Topic, id: topicId },
      ],
    }),
  }),
});

export const {
  useTopicsOverviewQuery,
  useTopicAnalysisQuery,
  useTopicsCustomerExperienceTrendQuery,
  useTopicsWinRateTrendQuery,
  useTopicDrilldownQuery,
  useTopicMatchesQuery,
} = topicApi;
